import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import { useAPI } from '@axo/shared/data-access/provider';
import { loan_quote_api } from '@axo/shared/data-access/api';
import { queryFunction } from '../query-function';
import { loanQuoteKeys, parseLoanQuoteKey } from '../cache-keys/loanQuoteKeys';
import {
  LoanQuoteRequirement,
  LoanQuoteRequirementRequest,
} from '@axo/shared/data-access/types/loan_quote';
import { applicationKeys } from '../cache-keys/applicationKeys';

export function useLoanQuoteRequirements(loanQuoteID: string) {
  const api = useAPI();
  const cacheKey = loanQuoteKeys.loanQuoteRequirements({
    loanQuoteID,
  });

  return useQuery(
    cacheKey,
    queryFunction(loan_quote_api.requirementLoanQuote(api), ({ queryKey }) =>
      parseLoanQuoteKey(queryKey)
    ),
    {
      enabled: !!loanQuoteID,
    }
  );
}

export function useBatchLoanQuoteRequirements(loanQuoteIDs: string[]) {
  const api = useAPI();
  return useQueries(
    loanQuoteIDs.map((loanQuoteID) => ({
      queryKey: loanQuoteKeys.loanQuoteRequirements({
        loanQuoteID,
      }),
      queryFn: () => loan_quote_api.requirementLoanQuote(api)({ loanQuoteID }),
      enabled: !!loanQuoteID,
    }))
  ) as UseQueryResult<LoanQuoteRequirement[]>[];
}

export function usePatchLoanQuoteRequirements(loanQuoteID: string) {
  const api = useAPI();
  const cacheKey = loanQuoteKeys.loanQuoteRequirements({
    loanQuoteID,
  });

  return useQuery(
    cacheKey,
    queryFunction(loan_quote_api.requirementLoanQuote(api), ({ queryKey }) =>
      parseLoanQuoteKey(queryKey)
    ),
    {
      enabled: !!loanQuoteID,
    }
  );
}

export function usePatchLoanQuote() {
  const api = useAPI();
  const client = useQueryClient();

  return useMutation(
    ({
      applicationID,
      loanQuoteID,
      requirementID,
      status,
    }: LoanQuoteRequirementRequest) =>
      loan_quote_api.patchLoanQuoteRequirements(api)({
        applicationID,
        loanQuoteID,
        requirementID,
        status,
      }),
    {
      onSuccess: (_, params) => {
        return client.invalidateQueries(
          applicationKeys.loanQuotes({
            applicationID: params.applicationID,
          })
        );
      },
    }
  );
}
